/**
 * The pure derivations behind the three Lost Sales pages — the census, the potential by door and
 * the recovery bridge — in one module, so the pages and their route exports compute each line the
 * same way (G2.4: an export reflects the view). Moved out of `LostSalesScreen.tsx` when client
 * rule 3 split the screen into Census · Potential · Explorer (DEC-88, ADR 8001); nothing in them
 * changed.
 */

import type { DemandPanelCell, LostSalesPotentialRow, StateCensusRow } from '@/api/generated';
import type { VarianceBridge } from '@/planning';

import { RECOVERED_UNITS } from './api';

/** Floating-point slack when checking observed + recovered = unconstrained (units, not money). */
const RECONCILE_TOLERANCE = 1e-6;

/** The potential classes' vocabulary members (bata-platform ADR 8003, 8004). */
export const ASSORTMENT_CLASS = 'assortment';
export const LATE_RANGING_CLASS = 'late_ranging';

/** One class's figure at one door: the units, absent, or ambiguous across runs. */
export type PotentialFigure =
  | { readonly kind: 'units'; readonly units: number; readonly runId: string }
  | { readonly kind: 'absent' }
  | { readonly kind: 'severalRuns' };

export interface PotentialLine {
  readonly locationKey: string;
  readonly assortment: PotentialFigure;
  readonly lateRanging: PotentialFigure;
}

const ABSENT_FIGURE: PotentialFigure = { kind: 'absent' };

function mergeFigure(current: PotentialFigure, row: LostSalesPotentialRow): PotentialFigure {
  if (current.kind === 'absent') return { kind: 'units', units: row.potentialUnits, runId: row.potentialRunId };
  if (current.kind === 'units' && current.runId === row.potentialRunId) return current;
  return { kind: 'severalRuns' };
}

/** The door lines a `?store=` scope keeps; an empty scope keeps every door (not narrowed). */
export function doorsInScope(lines: readonly PotentialLine[], stores: readonly string[]): readonly PotentialLine[] {
  return stores.length === 0 ? lines : lines.filter((line) => stores.includes(line.locationKey));
}

/**
 * DERIVE — one line per door, one figure per potential class, sorted by door. A class absent
 * at a door is absent, not zero; two effective runs of one class at one door are shown as
 * that, not summed (they may be two windows, or two opinions of one). A class outside the
 * two the page shows is not drawn: it would need a column of its own.
 */
export function potentialLines(rows: readonly LostSalesPotentialRow[]): readonly PotentialLine[] {
  const byDoor = new Map<string, { assortment: PotentialFigure; lateRanging: PotentialFigure }>();
  for (const row of rows) {
    const line = byDoor.get(row.locationKey) ?? { assortment: ABSENT_FIGURE, lateRanging: ABSENT_FIGURE };
    if (row.lostSalesClass === ASSORTMENT_CLASS) line.assortment = mergeFigure(line.assortment, row);
    else if (row.lostSalesClass === LATE_RANGING_CLASS) line.lateRanging = mergeFigure(line.lateRanging, row);
    else continue;
    byDoor.set(row.locationKey, line);
  }
  return [...byDoor.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([locationKey, line]) => ({ locationKey, ...line }));
}

export function figureValue(figure: PotentialFigure): number | null {
  return figure.kind === 'units' ? figure.units : null;
}

/** One class over the doors drawn: its units summed over the doors with ONE estimate of it. */
export interface ClassTotal {
  readonly units: number;
  /** Doors with one estimate of the class — the doors the sum is over. */
  readonly doors: number;
}

export interface PotentialSummary {
  readonly assortment: ClassTotal;
  readonly lateRanging: ClassTotal;
  /** Doors where one class has two effective runs — in neither sum, never summed. */
  readonly ambiguousDoors: number;
  /** The doors with an assortment estimate, most potential first (then by door key). */
  readonly assortmentRanked: readonly { readonly locationKey: string; readonly units: number }[];
}

function classTotal(lines: readonly PotentialLine[], pick: (line: PotentialLine) => PotentialFigure): ClassTotal {
  let units = 0;
  let doors = 0;
  for (const line of lines) {
    const figure = pick(line);
    if (figure.kind === 'units') {
      units += figure.units;
      doors += 1;
    }
  }
  return { units, doors };
}

/**
 * DERIVE — the page's figures, one class at a time. A class's sum over doors is that class's own
 * total, formed as the batch's control totals are (summed over doors); the two classes are never
 * added to each other, and a door with two runs of one class is in neither sum.
 */
export function potentialSummary(lines: readonly PotentialLine[]): PotentialSummary {
  const assortmentRanked = lines
    .flatMap((line) => (line.assortment.kind === 'units' ? [{ locationKey: line.locationKey, units: line.assortment.units }] : []))
    .sort((a, b) => b.units - a.units || (a.locationKey < b.locationKey ? -1 : a.locationKey > b.locationKey ? 1 : 0));
  return {
    assortment: classTotal(lines, (line) => line.assortment),
    lateRanging: classTotal(lines, (line) => line.lateRanging),
    ambiguousDoors: lines.filter((line) => line.assortment.kind === 'severalRuns' || line.lateRanging.kind === 'severalRuns').length,
    assortmentRanked,
  };
}

/** A named cut of the recovered-demand panel, summed over every cell the read returned. */
export interface CutTotals {
  readonly cells: number;
  readonly censoredCells: number;
  readonly observedUnits: number;
  readonly recoveredUnits: number;
  readonly unconstrainedUnits: number;
}

/**
 * DERIVE — the cut's totals. Sums over one run's cells for the named doors and weeks, formed as the
 * run's own control totals are (summed over persisted cells), so each is a sub-total of the same
 * quantity and not a new measure (the same rule as `panelBreakdown`).
 */
export function cutTotals(cells: readonly DemandPanelCell[]): CutTotals {
  return cells.reduce<CutTotals>((sum, cell) => ({
    cells: sum.cells + 1,
    censoredCells: sum.censoredCells + (cell.isCensored ? 1 : 0),
    observedUnits: sum.observedUnits + cell.observedUnits,
    recoveredUnits: sum.recoveredUnits + cell.recoveredUnits,
    unconstrainedUnits: sum.unconstrainedUnits + cell.unconstrainedDemandUnits,
  }), { cells: 0, censoredCells: 0, observedUnits: 0, recoveredUnits: 0, unconstrainedUnits: 0 });
}

export interface CutMap {
  /** The doors in the cut, by key. */
  readonly doors: readonly string[];
  /** The fiscal weeks in the cut, in label order (the platform's `YYYY-Www` sorts as time). */
  readonly weeks: readonly string[];
  /** Recovered units per door, per week; a door × week with no cell is absent, not zero. */
  readonly units: ReadonlyMap<string, ReadonlyMap<string, number>>;
  /** The door × week where recovery adds most, or `null` when it adds nothing anywhere. */
  readonly peak: { readonly door: string; readonly week: string; readonly units: number } | null;
}

const byKey = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** DERIVE — the cut's recovered units on a door × week grid, every article and size summed. */
export function cutMap(cells: readonly DemandPanelCell[]): CutMap {
  const units = new Map<string, Map<string, number>>();
  const weeks = new Set<string>();
  for (const cell of cells) {
    const door = units.get(cell.locationKey) ?? new Map<string, number>();
    door.set(cell.fiscalWeek, (door.get(cell.fiscalWeek) ?? 0) + cell.recoveredUnits);
    units.set(cell.locationKey, door);
    weeks.add(cell.fiscalWeek);
  }
  let peak: CutMap['peak'] = null;
  const doors = [...units.keys()].sort(byKey);
  const weekOrder = [...weeks].sort(byKey);
  for (const door of doors) {
    for (const week of weekOrder) {
      const value = units.get(door)?.get(week);
      if (value !== undefined && value > 0 && (peak === null || value > peak.units)) peak = { door, week, units: value };
    }
  }
  return { doors, weeks: weekOrder, units, peak };
}

/** The share of a class's units its first `top` doors hold, or `null` when there is nothing to share. */
export function topShare(ranked: readonly { readonly units: number }[], top: number): number | null {
  const total = ranked.reduce((sum, entry) => sum + entry.units, 0);
  if (total <= 0) return null;
  return ranked.slice(0, top).reduce((sum, entry) => sum + entry.units, 0) / total;
}

export interface CensusLine {
  readonly state: string;
  /** `null`: the run counted no store-week in this state — never 0. */
  readonly storeWeeks: number | null;
  readonly isModelled: boolean;
}

/**
 * DERIVE — the run's census in the vocabulary's order, every published state present. A state
 * the vocabulary publishes and the run did not count is a line with no count; a state the run
 * counted and the vocabulary does not list is kept, after the others, rather than dropped.
 */
export function censusLines(
  census: readonly StateCensusRow[],
  planningStates: readonly string[],
  modelledStates: readonly string[],
): readonly CensusLine[] {
  const counted = new Map(census.map((row) => [row.state, row]));
  const listed = planningStates.map((state): CensusLine => ({
    state,
    storeWeeks: counted.get(state)?.storeWeeks ?? null,
    isModelled: counted.get(state)?.isModelled ?? modelledStates.includes(state),
  }));
  const extra = census
    .filter((row) => !planningStates.includes(row.state))
    .map((row): CensusLine => ({ state: row.state, storeWeeks: row.storeWeeks, isModelled: row.isModelled }));
  return [...listed, ...extra];
}

/**
 * The observed → recovered → unconstrained bridge, or `null` when the three published totals
 * do not add up. The shared bridge refuses a residual (design/09 §8) — and a residual here is
 * not ours to explain, so the page states the three totals and the gap instead of drawing it.
 */
export function recoveryBridge(
  observed: number | null, recovered: number | null, unconstrained: number | null, recoveryRunId: string,
): VarianceBridge | null {
  if (observed === null || recovered === null || unconstrained === null) return null;
  if (Math.abs(observed + recovered - unconstrained) > RECONCILE_TOLERANCE) return null;
  return {
    fromLabelKey: 'postseason.measure.observedUnits',
    fromValue: observed,
    toLabelKey: 'postseason.measure.unconstrainedUnits',
    toValue: unconstrained,
    steps: [{
      id: 'recovered',
      labelKey: 'postseason.measure.recoveredUnits',
      delta: recovered,
      // The run that produced the figure. The semantic-layer definition, register version
      // and plan version are not published with the control totals, so they are left blank
      // rather than filled with something plausible.
      lineage: { measure: RECOVERED_UNITS, definition: '', registerVersion: '', sourceBatch: recoveryRunId, planVersionId: '' },
    }],
    unitKey: 'postseason.unit.units',
    polarity: 'none',
  };
}
